import {
  DialogActions,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { assginCreditLine } from 'lib/api';
import { getCreditLineFactoringSelectedDecisionEngines } from 'lib/api/decisionEngine';
import ExtendedDatePicker from 'lib/common/ExtendedDatePicker';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import { formatMoney } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import ConfirmAssignationDialog, {
  DecisionEngineOptions,
} from '../Common/ConfirmAssignationDialog';

const ConfirmingAssignationModule = (props: any) => {
  const {
    setOpenShoppingCart,
    openRequest,
    updateStatus,
    setInAssignation,
    setUp,
    onCancel,
  } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [amount, setAmount] = useState(openRequest.amount);
  const [isContractSigned, setIsContractSigned] = useState(false);
  const [contractExpirationDate, setContractExpirationDate] =
    useState<Date | null>(null);
  const [validUntil, setValidUntil] = useState(openRequest.validUntil);
  const [canUseDecisionEngine, setCanUseDecisionEngine] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [observation, setObservation] = useState(
    openRequest.observations || '',
  );

  const validateAmount = () => {
    if (amount <= 0) {
      alert('El monto del total de la línea debe ser mayor a 0');
      return false;
    }
    return true;
  };

  const validateContract = () => {
    if (isContractSigned && !contractExpirationDate) {
      alert(
        'Si el contrato está firmado, debes especificar la fecha de vencimiento del contrato',
      );
      return false;
    }
    return true;
  };

  const handlePreAssignation = async () => {
    if (!validateAmount()) return;
    setLoading(true);
    const response =
      await getCreditLineFactoringSelectedDecisionEngines(dispatch);
    response.forEach((engine: any) => {
      if (engine.revisionObjectiveId === 'creditLine') {
        setCanUseDecisionEngine(true);
      }
    });
    setLoading(false);
    setConfirmationDialog(true);
  };

  const handleSubmit = async ({
    clientDecisionEngineId,
  }: DecisionEngineOptions) => {
    if (!validateAmount()) return;
    if (!validateContract()) return;
    if (!validUntil)
      return alert('Por favor, selecciona una fecha de vencimiento');

    setLoading(true);
    try {
      await assginCreditLine(dispatch, {
        id: openRequest.id,
        amount,
        validUntil,
        isContractSigned,
        contractDueDate: contractExpirationDate,
        updateStatus,
        productType: openRequest.productType,
        clientDecisionEngineId,
        typeId: openRequest.productTypeId,
        observations: observation,
      });
      setInAssignation(false);
      showAlert(dispatch, 'success', 'Se ha asignado la línea con éxito.');
      await setUp();
      setOpenShoppingCart(false);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al asignar la línea');
    } finally {
      setLoading(false);
      setConfirmationDialog(false);
    }
  };

  const daysTillExpiration = validUntil
    ? (new Date(validUntil).getTime() - new Date().getTime()) /
      (1000 * 3600 * 24)
    : 0;
  const [days, setDays] = useState(Math.floor(daysTillExpiration));

  const handleChangeAmount = (value: any) => {
    const cleanValue = value.split('.').join('').split('$').join('');
    if (isNaN(cleanValue)) return;
    if (cleanValue < 0) return;
    setAmount(Number(cleanValue));
  };

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }
  return (
    <div>
      <div style={{ padding: '10px 0px' }}>
        <Typography variant="body1">
          Para asignar una línea de <b>Confirming</b> debes ingresar el monto y
          su vencimiento.
        </Typography>
      </div>
      <div style={{ padding: '10px 0px' }}>
        <Grid container spacing={2} alignItems={'top'}>
          <Grid item xs={4}>
            <Typography fontWeight={600} variant="body1">
              Monto total
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="description"
              label="Monto"
              type="text"
              value={formatMoney(amount)}
              onChange={(e) => handleChangeAmount(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Typography fontWeight={600} variant="body1">
                Contrato firmado
              </Typography>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexGrow: 1,
                  gap: '10px',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isContractSigned}
                      onChange={(e) => setIsContractSigned(e.target.checked)}
                    />
                  }
                  label={isContractSigned ? 'Sí' : 'No'}
                />
                {isContractSigned && (
                  <TextField
                    id="contractExpiration"
                    label="Fecha de vencimiento"
                    type="date"
                    value={
                      contractExpirationDate
                        ? contractExpirationDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setContractExpirationDate(
                        e.target.value ? new Date(e.target.value) : null,
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              </div>
            </div>
          </Grid>
          <ExtendedDatePicker
            validUntil={validUntil}
            setValidUntil={setValidUntil}
            days={days}
            setDays={setDays}
          />
          <Grid item xs={12}>
            <Typography fontWeight={600} variant="body1">
              Observación
            </Typography>
            <TextField
              label=""
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
            />
          </Grid>
        </Grid>
      </div>
      <DialogActions>
        <CustomButton
          onClick={() => (onCancel ? onCancel() : setOpenShoppingCart(false))}
          color="secondary"
        >
          Cancelar
        </CustomButton>
        <CustomButton onClick={() => handlePreAssignation()} color="primary">
          Enviar
        </CustomButton>
      </DialogActions>
      <ConfirmAssignationDialog
        open={confirmationDialog}
        onClose={() => setConfirmationDialog(false)}
        canUseClientDecisionEngine={canUseDecisionEngine}
        handleCancel={() => setConfirmationDialog(false)}
        handleSubmit={(opts) => void handleSubmit(opts)}
      />
    </div>
  );
};

export default ConfirmingAssignationModule;
